import { useMemo, useState } from 'react'
import { NavBar } from 'components/NavBar/NavBar'
import {
    Flex,
    HeadlineFlex,
    MainHeadline,
    PageFooter,
    PageWrapper,
    Table,
    Th,
    Tr,
} from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Td } from 'components/Td/Td'
import { Options } from 'components/Options/Options'
import { Pagination } from 'components/Pagination/Pagination'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { formatDateForDisplay, formatDateWithoutTimezone, getParams, handlePagination, months } from 'utils/helpers'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearPayrolls, deletePayroll, getPayrolls } from 'features/payrolls/payrollsSlice'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import { CreatePayrollModal } from './CreatePayrollModal'
import { ViewPayrollModal } from './ViewPayrollModa'
import { setConfirmModal } from 'features/global/globalSlice'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { CircleButton } from 'pages/Shifts/Shifts.styled'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'

export const Payrolls = () => {
    const { payrolls, totalPages } = useSelector((state: RootState) => state.payrolls)
    const [checked, setChecked] = useState([])
    const [params, setParams] = useState<any>(getParams({
        location, limit: 30, ap: {
            date: formatDateWithoutTimezone(new Date(new Date().getFullYear(), new Date().getMonth() == 0 ? 11 : new Date().getMonth() - 1, 1))
        }
    }))
    const [createModal, setCreateModal] = useState(false)
    const [infoModal, setInfoModal] = useState({ open: false, id: null })
    const dispatch = useDispatch<AppDispatch>()
    useQueryPagination({ fetchFunctions: [getPayrolls], cleanUpFunctions: [clearPayrolls], params })

    const toggleAll = () => {
        if (checked.length < payrolls.length) {
            for (const a of payrolls) {
                const id = a.id

                if (!checked.includes(id)) {
                    setChecked((prev) => [...prev, id])
                }
            }
        } else {
            setChecked([])
        }
    }


    const toggle = (id: number) => {
        if (checked.includes(id)) {
            setChecked((prev) => {
                return prev.filter((item) => item !== id)
            })
        } else {
            setChecked((prev) => (!prev ? [id] : [...prev, id]))
        }
    }

    const refetch = () => {
        dispatch(getPayrolls(params))
    }

    const handleDate = (value: ISelectValue, name: string) => {
        setParams((prev) => {
            const copy = { ...prev }
            copy.page = 1
            copy.date = name == 'month' ? formatDateWithoutTimezone(new Date(new Date(params.date).getFullYear(), value.value, 1).toISOString()) : formatDateWithoutTimezone(new Date(value.value, new Date(params.date).getMonth(), 1).toISOString())
            return copy
        })
    }

    const parseRoles = (data: any) => {
        return data.data.map((item: any) => ({ label: item.name, value: item.id }))
    }

    const handleSelect = (value: ISelectValue) => {
        setParams((prev) => {
            const copy = { ...prev }
            copy.roleId = value.value
            copy.page = 1
            return copy
        })
    }

    const removePayroll = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(deletePayroll(checked.length > 2 ? checked : [id]))
                },
                word: 'delete payroll',
            }),
        )
        if (checked.length) {
            setChecked([])
        }
    }

    const getYearsFrom2022 = () => {
        const currentYear = new Date().getFullYear();
        const years = [];

        for (let year = 2022; year <= currentYear; year++) {
            years.push({ label: String(year), value: year });
        }

        return years;
    }


    const defaultMonth = useMemo(() => {
        const month = new Date().getMonth()
        return month == 0 ? 11 : (month - 1)
    }, [])

    return (
        <>
            <NavBar defaultValue={params.q} input placeholder='Search Payrolls...' setParams={setParams} limit={30} />
            <PageWrapper>
                <HeadlineFlex width='100%' between mb='16' >
                    <MainHeadline>Payrolls</MainHeadline>
                    <Flex gap='1rem' alignEnd  >
                        <HeadlineFlex gap='1rem' alignEnd >
                            <HonestSelectAsyncInput
                                link='roles'
                                placeholder='Search role'
                                name='roleId'
                                labelText='Role'
                                width='250px'
                                onChange={(value: ISelectValue) => handleSelect(value)}
                                parserFunction={parseRoles}
                                nomarg
                            />
                            <Flex gap='1rem'
                            >
                                <HonestSelectInput noX width='10rem' nomarg onChange={handleDate} name='month' defaultValue={defaultMonth} labelText='Month' options={months.map((m, i) => ({ label: m, value: i }))} />
                                <HonestSelectInput noX width='10rem' nomarg onChange={handleDate} name='year' defaultValue={new Date(params.date).getFullYear()} labelText='Year' options={getYearsFrom2022()} />
                            </Flex>
                        </HeadlineFlex>

                        <Button
                            variant='primary'
                            onClick={() => setCreateModal(true)}
                        >
                            Create Payroll
                        </Button>
                    </Flex>
                </HeadlineFlex>
                {!payrolls.length ? (
                    <NoItems title='Payrolls' />
                ) : (
                    <Table>
                        <Th next>
                            <Td w='3%' noTooltip>
                                <CircleButton
                                    onClick={toggleAll}
                                    checked={checked.length === payrolls?.length}
                                />
                            </Td>
                            <Td w='14%' noTooltip text='Name' />
                            <Td w='13%' noTooltip text='Role' />
                            <Td w='13%' noTooltip text='Basic Salary' />
                            <Td w='13%' noTooltip text='Net Salary' />
                            <Td w='13%' noTooltip text='Total Cost' />
                            <Td w='10%' noTooltip text='From' />
                            <Td w='10%' noTooltip text='To' />
                            <Td w='11%' noTooltip text='' />
                            <Td w='5%' noTooltip text='' />
                        </Th>
                        {payrolls.map((item) => {
                            return (
                                <Tr view pointer key={item.id} onClick={() => setInfoModal({ open: true, id: item.id })} >
                                    <Td w='3%'>
                                        <CircleButton
                                            orange
                                            checked={checked.includes(item.id)}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                toggle(item.id)
                                            }}
                                        />
                                    </Td>
                                    <Td w='14%' text={item.user.firstName + ' ' + item.user.lastName} />
                                    <Td w='13%' text={item.user?.role?.name} />
                                    <Td w='13%' text={item.basicSalaryAmount ? ('GH₵ ' + item.basicSalaryAmount) : '/'} />
                                    <Td w='13%' text={item.netSalarySumAmount ? ('GH₵ ' + item.netSalaryAmount) : '/'} />
                                    <Td w='13%' text={item.totalCostAmount ? ('GH₵ ' + item.totalCostAmount) : '/'} />
                                    <Td w='10%' text={item.startDate ? formatDateForDisplay(item.startDate) : '/'} />
                                    <Td w='10%' text={item.endDate ? formatDateForDisplay(item.endDate) : '/'} />
                                    <Td w='11%' >{item.generationStatus == 2 ? <StatusPill status='danger'>Error</StatusPill> : <></>}</Td>
                                    <Td onClick={e => e.stopPropagation()} w='5%' options>
                                        <Options handleRemove={removePayroll} item={item} title='Payroll' />
                                    </Td>
                                </Tr>
                            )
                        })}
                    </Table>

                )}
                {createModal && <CreatePayrollModal refetch={refetch} openViewModal={() => setCreateModal(false)} closeModal={() => setCreateModal(false)} />}
                {infoModal.open && <ViewPayrollModal id={infoModal.id} closeModal={() => setInfoModal({ open: false, id: null })} />}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
